import { Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { DateRangeColumnFilter, MultiSelectColumnFilter } from "@/components/shared/column-filter";
import { type ColumnDefinition, ColumnManager } from "@/components/shared/column-manager";
import { NumberHoverCard } from "@/components/shared/number-hover-card";
import { nextSortState, SortableHeader, type SortDir } from "@/components/shared/sortable-header";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/ui/status-badge";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import type { AgencyTopPoliciesSortBy, AgencyTopPolicy } from "@/hooks/use-agency";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useTablePreferences } from "@/hooks/use-table-preferences";
import { formatCurrency, formatDate, formatWholeDollars } from "@/lib/format";
import { policyStatusKey } from "@/lib/status-colors";

/** Days between today (local midnight) and the expiration's calendar date.
 *  Compares date components only — avoids the off-by-one bug where the time
 *  portion of the timestamp would push the difference into the next bucket
 *  depending on what time of day the user loaded the page. */
function daysFromToday(expiration: string | null): number | null {
  if (!expiration) return null;
  const datePart = expiration.slice(0, 10); // "YYYY-MM-DD"
  const parts = datePart.split("-").map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return null;
  const expMidnight = new Date(y, m - 1, d).getTime();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((expMidnight - today.getTime()) / (1000 * 60 * 60 * 24));
}

// Managed secondary columns — shared across all three tabs. Policy Number is
// the sticky identity column (always first) and the per-tab value column is
// always pinned last, so neither is part of the column manager.
type SecondaryColumnKey = "insuredName" | "policyStatus" | "effectiveDate" | "expirationDate";

const DEFAULT_ORDER: readonly SecondaryColumnKey[] = [
  "insuredName",
  "policyStatus",
  "effectiveDate",
  "expirationDate",
];

const COLUMN_LABELS: Record<SecondaryColumnKey, string> = {
  insuredName: "Policyholder",
  policyStatus: "Status",
  effectiveDate: "Effective",
  expirationDate: "Expiration",
};

const COLUMN_DEFS: readonly ColumnDefinition<SecondaryColumnKey>[] = (
  Object.keys(COLUMN_LABELS) as SecondaryColumnKey[]
).map((key) => ({ key, label: COLUMN_LABELS[key] }));

type SortKey = SecondaryColumnKey | "policyNumber" | "value";

// The trailing column changes meaning per tab: written premium, incurred
// loss, or days-to-renewal. Each exposes a numeric sort value, a display
// label, and a formatter so the table body/header stay tab-agnostic.
function valueColumnFor(tab: AgencyTopPoliciesSortBy): {
  label: string;
  align: "right";
  numericOf: (p: AgencyTopPolicy) => number | null;
  render: (p: AgencyTopPolicy) => string;
  defaultDir: SortDir;
} {
  switch (tab) {
    case "premium":
      return {
        label: "Written Premium",
        align: "right",
        numericOf: (p) => p.writtenPremium,
        render: (p) => formatWholeDollars(p.writtenPremium),
        defaultDir: "desc",
      };
    case "incurred":
      return {
        label: "Incurred Loss",
        align: "right",
        numericOf: (p) => p.incurredLoss,
        render: (p) => formatCurrency(p.incurredLoss),
        defaultDir: "desc",
      };
    case "renewing":
      return {
        label: "Days to Renewal",
        align: "right",
        numericOf: (p) => daysFromToday(p.expirationDate),
        render: (p) => {
          const d = daysFromToday(p.expirationDate);
          return d == null ? "—" : `${d} day${d === 1 ? "" : "s"}`;
        },
        // Renewing-next ranks soonest first.
        defaultDir: "asc",
      };
  }
}

// nulls always sort last regardless of direction; non-null values follow dir.
function compareNullableNumbers(a: number | null, b: number | null, dir: SortDir): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  const base = a - b;
  return dir === "asc" ? base : -base;
}

function compareStrings(a: string, b: string, dir: SortDir): number {
  const cmp = a.localeCompare(b);
  return dir === "asc" ? cmp : -cmp;
}

export function AgencyTopPoliciesTable({
  rows,
  tab,
  agentNumber,
}: {
  rows: AgencyTopPolicy[];
  tab: AgencyTopPoliciesSortBy;
  agentNumber: string;
}) {
  const value = useMemo(() => valueColumnFor(tab), [tab]);

  const [filterText, setFilterText] = useState("");
  const debouncedFilter = useDebouncedValue(filterText, 200);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [effectiveDateRange, setEffectiveDateRange] = useState<{ from?: string; to?: string }>({});
  const [expirationDateRange, setExpirationDateRange] = useState<{ from?: string; to?: string }>(
    {},
  );
  // Default sort reproduces the tab's server-side ranking (the value column).
  const [sortKey, setSortKey] = useState<SortKey>("value");
  const [sortDir, setSortDir] = useState<SortDir>(value.defaultDir);

  const {
    orderedKeys: orderedSecondaryKeys,
    visibleKeys: visibleSecondaryKeys,
    isHidden: isSecondaryHidden,
    setHidden: setSecondaryHidden,
    setOrder: setSecondaryOrder,
    resetToDefaults: resetSecondaryColumns,
  } = useTablePreferences<SecondaryColumnKey>({
    tableId: "agency-top-policies",
    defaultOrder: DEFAULT_ORDER,
  });

  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of rows) {
      if (p.policyStatus) set.add(p.policyStatus);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  function handleSort(key: SortKey) {
    const next = nextSortState({ key: sortKey, dir: sortDir }, key, {
      effectiveDate: "desc",
      expirationDate: "desc",
      value: value.defaultDir,
    });
    setSortKey(next.key);
    setSortDir(next.dir);
  }

  const filtered = useMemo(() => {
    const term = debouncedFilter.trim().toLowerCase();
    const statusSet = new Set(statusFilter);
    const effFrom = effectiveDateRange.from;
    const effTo = effectiveDateRange.to;
    const expFrom = expirationDateRange.from;
    const expTo = expirationDateRange.to;

    const matched = rows.filter((p) => {
      if (statusSet.size > 0 && (!p.policyStatus || !statusSet.has(p.policyStatus))) return false;
      if (effFrom && (!p.effectiveDate || p.effectiveDate < effFrom)) return false;
      if (effTo && (!p.effectiveDate || p.effectiveDate > effTo)) return false;
      if (expFrom && (!p.expirationDate || p.expirationDate < expFrom)) return false;
      if (expTo && (!p.expirationDate || p.expirationDate > expTo)) return false;
      if (!term) return true;
      const number = p.policyNumber.toLowerCase();
      const name = (p.insuredName ?? "").toLowerCase();
      return number.includes(term) || name.includes(term);
    });

    const sorted = [...matched];
    sorted.sort((a, b) => {
      switch (sortKey) {
        case "value":
          return compareNullableNumbers(value.numericOf(a), value.numericOf(b), sortDir);
        case "policyNumber":
          return compareStrings(a.policyNumber, b.policyNumber, sortDir);
        default:
          return compareStrings(a[sortKey] ?? "", b[sortKey] ?? "", sortDir);
      }
    });
    return sorted;
  }, [
    rows,
    debouncedFilter,
    statusFilter,
    effectiveDateRange.from,
    effectiveDateRange.to,
    expirationDateRange.from,
    expirationDateRange.to,
    sortKey,
    sortDir,
    value,
  ]);

  const hasRefinement =
    debouncedFilter.trim().length > 0 ||
    statusFilter.length > 0 ||
    Boolean(effectiveDateRange.from) ||
    Boolean(effectiveDateRange.to) ||
    Boolean(expirationDateRange.from) ||
    Boolean(expirationDateRange.to);

  const renderSecondaryHeader = (key: SecondaryColumnKey) => {
    switch (key) {
      case "insuredName":
        return (
          <SortableHeader
            key={key}
            label="Policyholder"
            sortKey="insuredName"
            activeKey={sortKey}
            activeDir={sortDir}
            onSort={handleSort}
          />
        );
      case "policyStatus":
        return (
          <SortableHeader
            key={key}
            label="Status"
            sortKey="policyStatus"
            activeKey={sortKey}
            activeDir={sortDir}
            onSort={handleSort}
            filter={
              <MultiSelectColumnFilter
                ariaLabel="Filter by status"
                options={statusOptions}
                value={statusFilter}
                onChange={setStatusFilter}
              />
            }
          />
        );
      case "effectiveDate":
        return (
          <SortableHeader
            key={key}
            label="Effective"
            sortKey="effectiveDate"
            activeKey={sortKey}
            activeDir={sortDir}
            onSort={handleSort}
            filter={
              <DateRangeColumnFilter
                ariaLabel="Filter by effective date range"
                value={effectiveDateRange}
                onChange={setEffectiveDateRange}
              />
            }
          />
        );
      case "expirationDate":
        return (
          <SortableHeader
            key={key}
            label="Expiration"
            sortKey="expirationDate"
            activeKey={sortKey}
            activeDir={sortDir}
            onSort={handleSort}
            filter={
              <DateRangeColumnFilter
                ariaLabel="Filter by expiration date range"
                value={expirationDateRange}
                onChange={setExpirationDateRange}
              />
            }
          />
        );
    }
  };

  const renderSecondaryCell = (key: SecondaryColumnKey, p: AgencyTopPolicy) => {
    switch (key) {
      case "insuredName":
        return <TableCell key={key}>{p.insuredName ?? "—"}</TableCell>;
      case "policyStatus":
        return (
          <TableCell key={key}>
            {p.policyStatus ? (
              <StatusBadge statusKey={policyStatusKey(p.policyStatus)} label={p.policyStatus} />
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </TableCell>
        );
      case "effectiveDate":
        return (
          <TableCell key={key} className="text-muted-foreground">
            {formatDate(p.effectiveDate)}
          </TableCell>
        );
      case "expirationDate":
        return (
          <TableCell key={key} className="text-muted-foreground">
            {formatDate(p.expirationDate)}
          </TableCell>
        );
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-muted-foreground text-xs">
          {filtered.length === rows.length
            ? `${rows.length} polic${rows.length === 1 ? "y" : "ies"}`
            : `${filtered.length} of ${rows.length} polic${rows.length === 1 ? "y" : "ies"}`}
        </span>
        <div className="flex items-center gap-2">
          <div className="relative w-44 sm:w-56">
            <Search className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-2.5 size-3.5 text-muted-foreground" />
            <Input
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="Search..."
              className="h-7 pl-8 text-xs"
            />
          </div>
          <ColumnManager<SecondaryColumnKey>
            columns={COLUMN_DEFS}
            orderedKeys={orderedSecondaryKeys}
            isHidden={isSecondaryHidden}
            onReorder={setSecondaryOrder}
            onToggleHidden={setSecondaryHidden}
            onReset={resetSecondaryColumns}
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="py-6 text-center text-muted-foreground text-sm">
          {hasRefinement
            ? "No policies match the current filters."
            : tab === "renewing"
              ? "No upcoming renewals for this agency."
              : "No policies found for this agency."}
        </p>
      ) : (
        <Table>
          <TableHeader className="bg-card">
            <TableRow className="hover:bg-transparent">
              <SortableHeader
                label="Policy Number"
                sortKey="policyNumber"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={handleSort}
                className="sticky left-0 z-20 bg-card"
              />
              {visibleSecondaryKeys.map(renderSecondaryHeader)}
              <SortableHeader
                label={value.label}
                sortKey="value"
                activeKey={sortKey}
                activeDir={sortDir}
                onSort={handleSort}
                align="right"
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.policyNumber} className="hover:bg-muted/30">
                <TableCell className="sticky left-0 z-10 bg-card font-medium text-primary">
                  <NumberHoverCard
                    preview={{
                      kind: "policy",
                      number: p.policyNumber,
                      insuredName: p.insuredName,
                      status: p.policyStatus,
                      effectiveDate: p.effectiveDate,
                      expirationDate: p.expirationDate,
                    }}
                    copyLabel={`policy ${p.policyNumber}`}
                  >
                    <Link
                      to="/policy/$policyNumber"
                      params={{ policyNumber: p.policyNumber }}
                      search={{ from: `agency:${agentNumber}` }}
                      className="hover:underline"
                    >
                      {p.policyNumber}
                    </Link>
                  </NumberHoverCard>
                </TableCell>
                {visibleSecondaryKeys.map((key) => renderSecondaryCell(key, p))}
                <TableCell className="text-right font-medium tabular-nums">
                  {value.render(p)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
